import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { Link } from 'lego-on-react';
import { useCallback } from 'react';
import * as React from 'react';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { CheckboxDecorator } from '../../components/CheckboxDecorator/CheckboxDecorator';
import { Confirm } from '../../components/Confirm/Confirm';
import { Shape2, Shape2Icon } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { CanvasEditableLabelContainer } from '../CanvasEditableLabel/CanvasEditableLabelContainer';
import { CommonCreativeRenderer } from '../CommonCreativeRenderer/CommonCreativeRenderer';
import { CommonCreativesSelector } from '../CommonCreativesSelector/CommonCreativesSelector';
import { InfiniteList } from '../InfiniteList/InfiniteList';

import './BatchesList.css';
import * as keyset from './BatchesList.i18n';
import { BatchesListProps, CreativesBatch, TriState } from './typings';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);
const BLOCK = 'batches-list';

type BatchOverlayControlsProps = {
    applyEnabled: boolean;
    onApplyClick: (id: string) => void;
    create?: (id: string) => void;
} & Pick<CreativesBatch, 'id'>;

export const BatchOverlayControls = React.memo(
    ({ create, id, applyEnabled, onApplyClick }: BatchOverlayControlsProps) => {
        const onClick = useCallback(() => {
            if (applyEnabled) {
                onApplyClick(id);
            }
        }, [applyEnabled, id]);
        const onCreate = useCallback(() => {
            if (create) {
                create(id);
            }
        }, [id]);

        return (
            <>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'apply')}
                    disabled={!applyEnabled}
                    theme={'action'}
                    type={'link'}
                    onClick={onClick}
                    size={'n'}
                    text={i18n('apply')}
                />
                {create && (
                    <ButtonDecorator
                        theme={'normal'}
                        type={'link'}
                        onClick={onCreate}
                        size={'n'}
                        text={i18n('create-from-preset')}
                    />
                )}
            </>
        );
    }
);

class BatchControls extends React.PureComponent<{
    batch: CreativesBatch;
    onSaveName: (id: string, val: string) => void;
    showCreativesSelector: (id: string) => void;
    onShowDeleteConfirm: (id: string) => void;
}> {
    render() {
        let counterLabel;
        let chooseLabel;
        const { batch } = this.props;
        const selectedTotal = batch.items.filter(item => item.selected).length;
        if (batch.filteredBySizeTotal === 0) {
            counterLabel = batch.filteredBySizeTotal;
            chooseLabel = i18n('view');
        } else if (batch.selected === TriState.FALSE) {
            counterLabel = batch.filteredBySizeTotal;
            chooseLabel = i18n('select');
        } else {
            counterLabel = i18n('selected', {
                selected: selectedTotal,
                total: batch.items.length
            });
            chooseLabel = i18n('edit');
        }

        return (
            <>
                <div className={Bem.cls(BLOCK, 'controls')}>
                    <CanvasEditableLabelContainer
                        cls={Bem.cls(BLOCK, 'name')}
                        savedValue={batch.name}
                        editorId={batch.id}
                        onValueSaved={this.onSaveName}
                    />
                    <ButtonDecorator theme={'clear'} cls={Bem.cls(BLOCK, 'delete')} onClick={this.onDeleteClick}>
                        <Shape2 width={12} height={12} icon={Shape2Icon.trashbin} />
                    </ButtonDecorator>
                </div>
                <div>
                    <div className={Bem.cls(BLOCK, 'total')}>{counterLabel}</div>
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'choose')}
                        text={chooseLabel}
                        theme={'action'}
                        size={'s'}
                        onClick={this.onChooseSizeClick}
                    />
                </div>
            </>
        );
    }

    private onSaveName = (val: string) => {
        this.props.onSaveName(this.props.batch.id, val);
    };

    private onDeleteClick = () => {
        this.props.onShowDeleteConfirm(this.props.batch.id);
    };

    private onChooseSizeClick = () => {
        this.props.showCreativesSelector(this.props.batch.id);
    };
}

class BatchRenderer extends React.PureComponent<{
    batch: CreativesBatch;
    showDeleteConfirm: (id: string) => void;
    showCreativesSelector: (id: string) => void;
    setSelection: (id: string, val: boolean) => void;
    saveName: (id: string, val: string) => void;
    create?: (id: string, isAdaptive?: boolean) => void;
}> {
    render() {
        const { batch, showDeleteConfirm, showCreativesSelector } = this.props;
        const rendererData = {
            thumbnail: batch.thumbnail,
            alt: ''
        };
        const batchChecked = batch.selected === TriState.TRUE || batch.selected === TriState.INDETERMINATE;

        return (
            <div className={Bem.cls(BLOCK, 'item')}>
                <CheckboxDecorator
                    cls={Bem.cls(BLOCK, 'checkbox', {
                        indeterminate: batch.selected === TriState.INDETERMINATE
                    })}
                    theme={'normal'}
                    checked={batchChecked}
                    size={'m'}
                    item={batch}
                    disabled={batch.filteredBySizeTotal === 0}
                    onChecked={this.toggleItemSelection}
                />
                <CommonCreativeRenderer
                    cls={Bem.cls({
                        block: BLOCK,
                        elem: 'renderer',
                        mods: {
                            mode: batch.selected === TriState.FALSE ? 'default' : 'selected'
                        }
                    })}
                    item={rendererData}
                    controls={
                        <BatchOverlayControls
                            id={batch.id}
                            create={this.props.create ? this.onCreateBatch : undefined}
                            applyEnabled={batch.filteredBySizeTotal > 0}
                            onApplyClick={this.onApplyClick}
                        />
                    }
                />
                <BatchControls
                    batch={batch}
                    onSaveName={this.onSaveName}
                    onShowDeleteConfirm={showDeleteConfirm}
                    showCreativesSelector={showCreativesSelector}
                />
            </div>
        );
    }

    private onCreateBatch = (id: string) => {
        if (!this.props.create) {
            return;
        }

        // достаточно вытащить первый элемент, тк у адаптивов он всегда один
        const isAdaptive = Boolean(this.props.batch.items.length && this.props.batch.items[0].data.isAdaptive);
        this.props.create(id, isAdaptive);
    };

    private onApplyClick = (id: string) => {
        this.props.setSelection(id, true);
    };

    private onSaveName = (id: string, val: string) => {
        this.props.saveName(id, val);
    };

    private toggleItemSelection = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean; item: CreativesBatch }
    ) => {
        this.props.setSelection(data.item.id, data.checked);
    };
}

export class BatchesList extends React.PureComponent<BatchesListProps> {
    render() {
        const {
            collection: { batches },
            presetsUrl,
            isBusy,
            deleteConfirmVisible,
            hideDeleteConfirm,
            creativesSelectorDataProvider,
            toggleSizesSelection,
            hideCreativesSelector,
            creativesSelectorVisible
        } = this.props;
        let content;

        if (batches.length > 0 || isBusy) {
            content = (
                <>
                    {deleteConfirmVisible && (
                        <Confirm
                            message={i18n('creative-remove-confirm-description')}
                            onSuccessClick={this.props.delete}
                            onDeclineClick={hideDeleteConfirm}
                        />
                    )}
                    {creativesSelectorVisible && (
                        <CommonCreativesSelector
                            items={creativesSelectorDataProvider}
                            toggle={toggleSizesSelection}
                            submit={this.submitSizesSelection}
                            cancel={hideCreativesSelector}
                            close={hideCreativesSelector}
                        />
                    )}
                    {this.props.filteredSizes && this.props.filteredSizes.length > 0 && (
                        <div className={Bem.cls(BLOCK, 'sizes')}>
                            {i18n('selected-banner-sizes', {
                                sizes: this.props.filteredSizes.join(', ')
                            })}
                        </div>
                    )}
                    <div className={Bem.cls(BLOCK, 'infinite-list-wrapper')}>
                        <InfiniteList content={this.getContent()} scrollAtBottom={this.scrollAtBottom} />
                    </div>
                </>
            );
        } else {
            content = (
                <div className={Bem.cls(BLOCK, 'empty-message')}>
                    {presetsUrl
                        ? i18nRaw('no-creatives-but-can-create', {
                              presetsLink: (
                                  <Link key={'link'} theme={'normal'} url={presetsUrl}>
                                      {i18n('create')}
                                  </Link>
                              )
                          })
                        : i18nRaw('no-creatives')}
                </div>
            );
        }

        return <div className={classNames(this.props.cls, BLOCK)}>{content}</div>;
    }

    private getContent() {
        const {
            collection: { batches },
            showDeleteConfirm,
            showCreativesSelector,
            setSelection,
            saveName,
            createBatch
        } = this.props;

        return (
            <>
                {batches.map(batch => {
                    return (
                        <BatchRenderer
                            key={batch.id}
                            batch={batch}
                            showDeleteConfirm={showDeleteConfirm}
                            showCreativesSelector={showCreativesSelector}
                            setSelection={setSelection}
                            saveName={saveName}
                            create={createBatch}
                        />
                    );
                })}
            </>
        );
    }

    private submitSizesSelection = () => {
        this.props.submitSizesSelection();
        this.props.hideCreativesSelector();
    };

    private scrollAtBottom = () => {
        const offset = this.props.collection.batches.length;
        if (offset < this.props.collection.total) {
            this.props.load(offset);
        }
    };
}
